import { refreshConnectorUsages } from "../refreshConnectorUsages";
import * as z from "zod";
import { assertSsoRouteProviders } from "../ssoRouteDependencies";
import { userFacingError } from "../authorization";
import { copyAllCommitFiles, findConflictedFiles } from "../commitFiles";
import { copyChatHistory } from "../copyChatHistory";
import { insertRows } from "../insertRows";
import { type Schema, type Workflow, zql } from "../generatedDrizzleSchema";
import { mergeMainIfBehind } from "../mergeCore";
import { inheritBranchContributors } from "../inheritBranchContributors";
import { actorColumns, creatorColumns } from "../rbac/roles";
import { assertNotSelfApproval } from "./assertNotSelfApproval";
import { mutator } from "./mutator";
import { provideSpaceChatToolResult } from "./provideSpaceChatToolResult";
import { assertNotGitSynced } from "./syncGit";
import { generateId } from "../id";
import { clientGeneratableIdSchema } from "../id";
import type { Context } from "../authorization";
import type { Transaction } from "@rocicorp/zero";

export const pushBranchToLiveSchema = z.object({
  sourceBranchId: z.string(),
  newCommitId: clientGeneratableIdSchema,
  newChatId: clientGeneratableIdSchema,
  message: z.string(),
  copyVolumesToLive: z.boolean().optional(),
  toolResult: z
    .object({
      chatId: z.string(),
      entryId: z.string(),
      outputId: z.string(),
    })
    .optional(),
});

export async function requirePushLiveToolCall(
  tx: Transaction<Schema>,
  entryId: string
) {
  const call = await tx.run(zql.toolCalls.where("entryId", entryId).one());
  if (call?.toolName !== "pushLive")
    throw userFacingError("Push live request not found");
}

export const pushBranchToLive = mutator({
  schema: pushBranchToLiveSchema,
  resolve: {
    sourceBranch: { kind: "branch", from: "sourceBranchId" },
  },
  permissions: [
    {
      permission: "space:push_live",
      scopeId: ({ args }) => args.sourceBranch.workflow.spaceId,
    },
  ],
  fn: async (opts) => {
    if (opts.args.toolResult) {
      await requirePushLiveToolCall(opts.tx, opts.args.toolResult.entryId);
      await provideSpaceChatToolResult.fn({
        tx: opts.tx,
        ctx: opts.ctx,
        args: {
          ...opts.args.toolResult,
          branchId: opts.args.sourceBranch.id,
          pauseAfter: true,
        },
      });
    }
    await assertNotGitSynced(opts.tx, opts.args.sourceBranch.workflow.spaceId);
    const approvedHeadCommitId = opts.args.sourceBranch.headCommitId;
    const headCommitId = await mergeMainIfBehind(opts.tx, {
      branch: opts.args.sourceBranch,
      creator: creatorColumns(opts.ctx),
      tenantId: opts.ctx.tenantId,
    });
    await pushBranchToLiveInTransaction({
      ...opts,
      args: {
        ...opts.args,
        sourceBranch: { ...opts.args.sourceBranch, headCommitId },
      },
      approvedHeadCommitId,
    });
  },
});

function listFiles(paths: string[]): string {
  if (paths.length <= 3) {
    if (paths.length === 1) return paths[0]!;
    return `${paths.slice(0, -1).join(", ")} and ${paths.at(-1)}`;
  }
  return `${paths.slice(0, 3).join(", ")} and ${paths.length - 3} more files`;
}

export async function pushBranchToLiveInTransaction({
  tx,
  ctx,
  args: { newCommitId, newChatId, message, sourceBranch },
  deleteSourceBranch = true,
  approvedHeadCommitId,
}: {
  tx: Transaction<Schema>;
  ctx: Context;
  args: {
    newCommitId: string;
    newChatId: string;
    message: string;
    sourceBranch: {
      id: string;
      name: string;
      workflowId: string;
      userId: string | null;
      headCommitId: string | null;
      workflow: Pick<Workflow, "autofixBranchId" | "autoImproveBranchId">;
    };
  };
  deleteSourceBranch?: boolean;
  approvedHeadCommitId?: string | null;
}) {
  if (sourceBranch.name === "main")
    throw userFacingError("Branch is already live");

  const creator = creatorColumns(ctx);
  const actor = actorColumns(ctx);
  const now = Date.now();
  // user_actions cannot attribute service accounts, so their marker is a system message.
  const markerEntryType = actor.userId ? "UserAction" : "SystemMessage";

  if (!sourceBranch.headCommitId) {
    throw userFacingError("Branch has no head commit");
  }

  const conflictedPaths = await findConflictedFiles({
    tx,
    commitId: sourceBranch.headCommitId,
  });
  if (conflictedPaths.length > 0) {
    throw userFacingError(
      `Conflicts in ${listFiles(conflictedPaths)} need to be resolved before pushing live.`
    );
  }

  // Both the optimistic and the authoritative server path run this; the
  // server result is what wins.
  await assertNotSelfApproval(
    tx,
    ctx,
    sourceBranch.id,
    sourceBranch.workflowId,
    approvedHeadCommitId
  );

  const [mainBranch] = await tx.run(
    zql.workflowBranches
      .where("workflowId", sourceBranch.workflowId)
      .where("name", "main")
  );

  const mainBranchId = mainBranch ? mainBranch.id : generateId();

  if (!mainBranch) {
    await tx.mutate.workflowBranches.insert({
      id: mainBranchId,
      workflowId: sourceBranch.workflowId,
      name: "main",
      headCommitId: newCommitId,
      createdAt: now,
      lastChatEntryAt: now,
      tenantId: ctx.tenantId,
    });
  }

  const commit = {
    id: newCommitId,
    workflowId: sourceBranch.workflowId,
    branchId: mainBranchId,
    parentCommitId: mainBranch?.headCommitId ?? null,
    chatId: newChatId,
    message,
    createdAt: now,
    ...creator,
    tenantId: ctx.tenantId,
  };
  const chat = {
    id: newChatId,
    commitId: newCommitId,
    createdAt: now,
    lastChatEntryType: markerEntryType,
    tenantId: ctx.tenantId,
  };

  // Zero’s insert skips existing IDs, so publish destinations must require creation.
  await insertRows(tx, "workflowCommits", [commit], { onConflict: "reject" });
  await insertRows(tx, "chats", [chat], { onConflict: "reject" });

  const facetCounts = await copyAllCommitFiles(
    { tx, commitId: sourceBranch.headCommitId, tenantId: ctx.tenantId },
    newCommitId
  );

  await assertSsoRouteProviders(tx, ctx.tenantId, newCommitId);

  const { entryCount, lastEntryId, legacyToolCallCopies } =
    await copyChatHistory({
      tx,
      sourceBranchId: sourceBranch.id,
      newChatId,
      tenantId: ctx.tenantId,
    });

  const actionEntryId = generateId();
  await tx.mutate.chatEntries.insert({
    id: actionEntryId,
    chatId: newChatId,
    entryType: markerEntryType,
    createdAt: now,
    previousEntryId: lastEntryId,
    tenantId: ctx.tenantId,
  });
  if (actor.userId) {
    await tx.mutate.userActions.insert({
      entryId: actionEntryId,
      userId: actor.userId,
      actionType: "pushDraftToLive",
      metadata: { draftName: sourceBranch.name, branchId: sourceBranch.id },
      tenantId: ctx.tenantId,
    });
  } else {
    await tx.mutate.systemMessages.insert({
      entryId: actionEntryId,
      text: `Pushed “${sourceBranch.name}” to live`,
      tenantId: ctx.tenantId,
    });
  }

  await inheritBranchContributors(tx, ctx, sourceBranch, mainBranchId);

  const [wf] = await tx.run(zql.workflows.where("id", sourceBranch.workflowId));

  // A merged draft is no longer the space branch's claim on this workflow; the
  // ref goes on showing the content by inheriting it from its own tree.
  const spaceBranchMemberships = await tx.run(
    zql.spaceBranchMembers.where("workflowId", sourceBranch.workflowId)
  );
  for (const member of spaceBranchMemberships) {
    if (member.workflowBranchId !== sourceBranch.id) continue;
    await tx.mutate.spaceBranchMembers.update({
      spaceBranchId: member.spaceBranchId,
      workflowId: member.workflowId,
      workflowBranchId: null,
    });
  }

  if (deleteSourceBranch) {
    await tx.mutate.workflowBranches.delete({ id: sourceBranch.id });
  }

  if (mainBranch) {
    await tx.mutate.workflowBranches.update({
      id: mainBranchId,
      headCommitId: newCommitId,
      lastChatEntryAt: now,
    });
    await refreshConnectorUsages(tx, newCommitId);
  }

  await tx.mutate.workflows.update({
    id: sourceBranch.workflowId,
    updatedAt: now,
    pausedAt: null,
    ...(wf?.autofixBranchId === sourceBranch.id
      ? { autofixBranchId: null }
      : {}),
    ...(wf?.autoImproveBranchId === sourceBranch.id
      ? { autoImproveBranchId: null }
      : {}),
  });

  return {
    branchId: mainBranchId,
    ...facetCounts,
    chatEntries: entryCount,
    // TOOL_CALL_OUTPUT_V1_BACKCOMPAT start
    legacyToolCallCopies,
    // TOOL_CALL_OUTPUT_V1_BACKCOMPAT end
  };
}
